import {
  Entity,
  parseEntityRef,
  RELATION_OWNED_BY,
  RELATION_PART_OF,
  stringifyEntityRef,
} from '@backstage/catalog-model';
import { humanizeEntityRef } from '@backstage/plugin-catalog-react';

/**
 * What the catalog list shows, and how its address says so
 * (specs/catalog-list.md).
 *
 * The list is narrowed four ways, and each sits below the one before it: a tab
 * (whose things), a type chip (what kind of thing), the filters added with
 * "+ Filter" (owner, system, lifecycle), and the text typed into the filter.
 * Every count is taken at its own level — a tab counts everything in it, a chip
 * everything in its tab — so a count never changes because of a choice made
 * below it.
 *
 * The address uses Backstage's own `filters[...]` vocabulary, the one the stock
 * catalog page read, because links into the catalog were written in it before
 * this page existed: the home page's services count asks for
 * `filters[user]=owned`, and a group page's ownership card for
 * `filters[kind]=…&filters[type]=…&filters[owners]=…`. A page that spoke a
 * vocabulary of its own would have broken every one of them without a sound.
 */

export type ListTab = 'owned' | 'starred' | 'all';

export const TABS: { id: ListTab; label: string }[] = [
  { id: 'owned', label: 'Mine' },
  { id: 'starred', label: 'Starred' },
  { id: 'all', label: 'All' },
];

export type TypeChip = 'all' | 'service' | 'website' | 'library' | 'api';

export const TYPE_CHIPS: { id: TypeChip; label: string }[] = [
  { id: 'all', label: 'All components' },
  { id: 'service', label: 'Service' },
  { id: 'website', label: 'Website' },
  { id: 'library', label: 'Library' },
  { id: 'api', label: 'API' },
];

/** The component types a chip of its own stands for. */
const CHIP_TYPES = ['service', 'website', 'library'] as const;

/**
 * The narrowings a chip does not express. `kind` and `type` only ever arrive
 * with a link — a component-only list from the home page, an API type from a
 * group page — and the rest can also be added with "+ Filter".
 */
export type FacetKey =
  'kind' | 'type' | 'owners' | 'systems' | 'lifecycles' | 'tags';

export const FACET_LABELS: Record<FacetKey, string> = {
  kind: 'Kind',
  type: 'Type',
  owners: 'Owner',
  systems: 'System',
  lifecycles: 'Lifecycle',
  tags: 'Tag',
};

/** The facets "+ Filter" offers, in the order it offers them. */
export const MENU_FACETS: FacetKey[] = ['owners', 'systems', 'lifecycles'];

export type Facets = Partial<Record<FacetKey, string[]>>;

export type ListFilters = {
  tab: ListTab;
  chip: TypeChip;
  facets: Facets;
  text: string;
};

/** One entry of the list, read once from its catalog entity. */
export type ListRow = {
  entity: Entity;
  /** The entity's ref, which is also what a star is stored under. */
  ref: string;
  kind: 'component' | 'api';
  name: string;
  namespace: string;
  /** Its title, else its name — what a reader recognises it by. */
  title: string;
  description?: string;
  /** What the Type column says: the component's type, or `api`. */
  category: string;
  /** The entity's own `spec.type`, lower-cased. */
  specType?: string;
  /** Every owner, as full refs, lower-cased. */
  owners: string[];
  /** The owner the row names — the first. */
  owner?: string;
  /** The system, as a full ref, lower-cased. */
  system?: string;
  lifecycle?: string;
  tags: string[];
};

const lower = (s: string) => s.toLocaleLowerCase('en-US');

/** A ref in its full, lower-cased form; the input itself if it is not one. */
export function fullRef(value: string, defaultKind: string): string {
  try {
    return lower(
      stringifyEntityRef(
        parseEntityRef(value, { defaultKind, defaultNamespace: 'default' }),
      ),
    );
  } catch {
    return lower(value);
  }
}

/** A full ref as a reader writes it: `platform-team`, `user:e2e`. */
export function shortRef(ref: string, defaultKind: string): string {
  try {
    return humanizeEntityRef(parseEntityRef(ref), { defaultKind });
  } catch {
    return ref;
  }
}

const relationTargets = (entity: Entity, type: string) =>
  (entity.relations ?? [])
    .filter(relation => relation.type === type)
    .map(relation => lower(relation.targetRef));

export function toRow(entity: Entity): ListRow {
  const spec = (entity.spec ?? {}) as Record<string, unknown>;
  const kind = lower(entity.kind) === 'api' ? 'api' : 'component';
  const specType =
    typeof spec.type === 'string' && spec.type ? lower(spec.type) : undefined;

  // The relations are the catalog's own reading of the entity and are what the
  // catalog filters on; the spec is the fallback for an entity the catalog has
  // not processed relations for.
  let owners = relationTargets(entity, RELATION_OWNED_BY);
  if (!owners.length && typeof spec.owner === 'string' && spec.owner) {
    owners = [fullRef(spec.owner, 'group')];
  }
  let system = relationTargets(entity, RELATION_PART_OF).find(ref =>
    ref.startsWith('system:'),
  );
  if (!system && typeof spec.system === 'string' && spec.system) {
    system = fullRef(spec.system, 'system');
  }

  return {
    entity,
    ref: stringifyEntityRef(entity),
    kind,
    name: entity.metadata.name,
    namespace: entity.metadata.namespace ?? 'default',
    title: entity.metadata.title || entity.metadata.name,
    description: entity.metadata.description || undefined,
    category: kind === 'api' ? 'api' : (specType ?? ''),
    specType,
    owners,
    owner: owners[0],
    system,
    lifecycle:
      typeof spec.lifecycle === 'string' && spec.lifecycle
        ? spec.lifecycle
        : undefined,
    tags: (entity.metadata.tags ?? []).filter(
      (tag): tag is string => typeof tag === 'string',
    ),
  };
}

/** Rows in the order the list shows them: by the name a reader sees. */
export const byTitle = (a: ListRow, b: ListRow) =>
  a.title.localeCompare(b.title, 'en', { sensitivity: 'base' }) ||
  a.ref.localeCompare(b.ref);

/* ── The address ───────────────────────────────────────────────────────── */

const FILTER_PARAM = /^filters\[(\w+)\](?:\[\d*\])?$/;

const unique = (values: string[]) => [
  ...new Set(values.map(v => v.trim()).filter(Boolean)),
];

/**
 * The filters an address asks for.
 *
 * `foreignKind` is set when it asks for a kind this list does not hold — a
 * system, a group, a template. Those addresses stay the stock catalog page's,
 * which lists every kind; answering them here with an empty list would turn a
 * group page's "3 systems" into a link to nothing.
 */
export function readFilters(params: URLSearchParams): {
  filters: ListFilters;
  foreignKind: boolean;
} {
  const raw: Record<string, string[]> = {};
  params.forEach((value, key) => {
    const match = FILTER_PARAM.exec(key);
    if (match) (raw[match[1]] ??= []).push(value);
  });

  const kinds = unique(raw.kind ?? []).map(lower);
  const types = unique(raw.type ?? []).map(lower);
  const foreignKind = kinds.some(
    kind => kind !== 'component' && kind !== 'api',
  );

  const user = lower(raw.user?.[0] ?? '');
  const tab: ListTab =
    user === 'owned' || user === 'starred' ? (user as ListTab) : 'all';

  const facets: Facets = {};
  let chip: TypeChip = 'all';
  const onlyComponents = kinds.length === 1 && kinds[0] === 'component';
  const onlyApis = kinds.length === 1 && kinds[0] === 'api';

  if (onlyApis) {
    chip = 'api';
    if (types.length) facets.type = types;
  } else if (
    (onlyComponents || kinds.length === 0) &&
    types.length === 1 &&
    (CHIP_TYPES as readonly string[]).includes(types[0])
  ) {
    chip = types[0] as TypeChip;
  } else {
    // A single kind narrows; both kinds together are the whole list.
    if (kinds.length === 1) facets.kind = kinds;
    if (types.length) facets.type = types;
  }

  for (const key of ['owners', 'systems', 'lifecycles', 'tags'] as const) {
    const values = unique(raw[key] ?? []);
    if (values.length) facets[key] = values;
  }

  return {
    filters: { tab, chip, facets, text: raw.text?.[0] ?? '' },
    foreignKind,
  };
}

/** The address for `filters`, keeping whatever else `current` carries. */
export function writeFilters(
  filters: ListFilters,
  current: URLSearchParams,
): URLSearchParams {
  const next = new URLSearchParams();
  current.forEach((value, key) => {
    if (!FILTER_PARAM.test(key)) next.append(key, value);
  });
  const put = (key: string, values: string[] = []) =>
    values.forEach(value => next.append(`filters[${key}]`, value));

  if (filters.tab !== 'all') put('user', [filters.tab]);
  if (filters.chip === 'api') {
    put('kind', ['api']);
  } else if (filters.chip !== 'all') {
    put('kind', ['component']);
    put('type', [filters.chip]);
  } else {
    put('kind', filters.facets.kind);
  }
  put('type', filters.facets.type);
  put('owners', filters.facets.owners);
  put('systems', filters.facets.systems);
  put('lifecycles', filters.facets.lifecycles);
  put('tags', filters.facets.tags);
  if (filters.text.trim()) put('text', [filters.text]);
  return next;
}

/* ── Matching ──────────────────────────────────────────────────────────── */

/** Who the reader is, as far as the tabs are concerned. */
export type ReaderContext = {
  /** The refs the reader owns through, lower-cased. */
  ownership: Set<string>;
  /** The refs the reader has starred. */
  starred: Set<string>;
};

export function inTab(row: ListRow, tab: ListTab, reader: ReaderContext) {
  if (tab === 'owned') return row.owners.some(o => reader.ownership.has(o));
  if (tab === 'starred') return reader.starred.has(row.ref);
  return true;
}

export function inChip(row: ListRow, chip: TypeChip) {
  if (chip === 'all') return true;
  if (chip === 'api') return row.kind === 'api';
  return row.kind === 'component' && row.specType === chip;
}

/** The value a row holds for a facet, in the form its matching compares. */
function rowValues(row: ListRow, key: FacetKey): string[] {
  switch (key) {
    case 'kind':
      return [row.kind];
    case 'type':
      return row.specType ? [row.specType] : [];
    case 'owners':
      return row.owners;
    case 'systems':
      return row.system ? [row.system] : [];
    case 'lifecycles':
      return row.lifecycle ? [lower(row.lifecycle)] : [];
    case 'tags':
      return row.tags.map(lower);
    default:
      return [];
  }
}

/** A value from the address, in the form `rowValues` holds it. */
function normalise(key: FacetKey, value: string): string {
  if (key === 'owners') return fullRef(value, 'group');
  if (key === 'systems') return fullRef(value, 'system');
  return lower(value);
}

/** One value of a facet the way the address writes it. */
export function facetValueOf(key: FacetKey, rowValue: string): string {
  if (key === 'owners') return shortRef(rowValue, 'group');
  if (key === 'systems') return shortRef(rowValue, 'system');
  return rowValue;
}

/**
 * The facets on the chips' own axis. A link can narrow by kind or by type in a
 * way no single chip expresses — components only, or APIs of one type — and
 * choosing a chip is choosing that axis afresh, so it replaces them.
 */
const CHIP_AXIS: FacetKey[] = ['kind', 'type'];

/** `facets` without the ones choosing a chip replaces. */
export function withoutChipAxis(facets: Facets): Facets {
  const rest = { ...facets };
  for (const key of CHIP_AXIS) delete rest[key];
  return rest;
}

/** Any of a facet's values matches; every facet has to. */
export function inFacets(
  row: ListRow,
  facets: Facets,
  except: FacetKey[] = [],
) {
  return (Object.keys(facets) as FacetKey[]).every(key => {
    const wanted = facets[key];
    if (except.includes(key) || !wanted?.length) return true;
    const held = rowValues(row, key);
    return wanted.some(value => held.includes(normalise(key, value)));
  });
}

/**
 * Whether a row matches what was typed: every word has to be found in its
 * name, title, tags, owner or system.
 */
export function matchesText(row: ListRow, text: string) {
  const words = lower(text).split(/\s+/).filter(Boolean);
  if (!words.length) return true;
  const haystack = [
    row.name,
    row.title,
    ...row.tags,
    ...row.owners.map(o => shortRef(o, 'group')),
    row.system ? shortRef(row.system, 'system') : '',
  ]
    .join(' ')
    .toLocaleLowerCase('en-US');
  return words.every(word => haystack.includes(word));
}

export type ListView = {
  tabCounts: Record<ListTab, number>;
  chipCounts: Record<TypeChip, number>;
  /** The rows the chosen tab, chip and filters hold. */
  inScope: ListRow[];
  /** Those of them the typed text matches — what the list shows. */
  shown: ListRow[];
};

export function viewOf(
  rows: ListRow[],
  filters: ListFilters,
  reader: ReaderContext,
): ListView {
  const tabCounts = { owned: 0, starred: 0, all: 0 };
  for (const row of rows) {
    for (const { id } of TABS) if (inTab(row, id, reader)) tabCounts[id] += 1;
  }

  const inScope = rows.filter(
    row =>
      inTab(row, filters.tab, reader) &&
      inFacets(row, filters.facets) &&
      inChip(row, filters.chip),
  );

  // A chip carries the number of entries it lists: the chosen one what is
  // listed now, every other one what choosing it would list — which, since
  // choosing a chip replaces a kind or a type a link narrowed to, is counted
  // without them. Counted with them, "API 0" beside a components-only link
  // would be a count that choosing API contradicts.
  const chipCounts = {} as Record<TypeChip, number>;
  for (const { id } of TYPE_CHIPS) {
    chipCounts[id] =
      id === filters.chip
        ? inScope.length
        : rows.filter(
            row =>
              inTab(row, filters.tab, reader) &&
              inFacets(row, filters.facets, CHIP_AXIS) &&
              inChip(row, id),
          ).length;
  }

  return {
    tabCounts,
    chipCounts,
    inScope,
    shown: inScope.filter(row => matchesText(row, filters.text)),
  };
}

/**
 * The values "+ Filter" offers for a facet, each with how many rows it would
 * leave — counted as though that facet were not set yet, so choosing a second
 * owner shows what it adds rather than a column of zeros.
 */
export function facetOptions(
  rows: ListRow[],
  filters: ListFilters,
  reader: ReaderContext,
  key: FacetKey,
): { value: string; count: number }[] {
  const counts = new Map<string, number>();
  for (const row of rows) {
    if (
      !inTab(row, filters.tab, reader) ||
      !inChip(row, filters.chip) ||
      !inFacets(row, filters.facets, [key])
    ) {
      continue;
    }
    for (const held of new Set(rowValues(row, key))) {
      counts.set(held, (counts.get(held) ?? 0) + 1);
    }
  }
  return [...counts.entries()]
    .map(([held, count]) => ({ value: facetValueOf(key, held), count }))
    .sort((a, b) => b.count - a.count || a.value.localeCompare(b.value));
}

/** Whether `value` is already chosen for `key`, in whichever form it came. */
export function isChosen(facets: Facets, key: FacetKey, value: string) {
  const target = normalise(key, value);
  return (facets[key] ?? []).some(v => normalise(key, v) === target);
}
